import type { Screen0LanguageCode } from "@/features/language/language-registry";

import { GERMAN_TRANSLATIONS } from "./de";
import { EN_TRANSLATIONS } from "./en";
import { ES_TRANSLATIONS } from "./es";
import { FR_TRANSLATIONS } from "./fr";
import { IT_TRANSLATIONS } from "./it";

type DeepWiden<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T extends readonly (infer Item)[]
        ? ReadonlyArray<DeepWiden<Item>>
        : T extends object
          ? { [Key in keyof T]: DeepWiden<T[Key]> }
          : T;

type UnknownRecord = Record<string, unknown>;

export type Screen0Messages = DeepWiden<typeof EN_TRANSLATIONS>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function collectMissingPaths(
  reference: unknown,
  candidate: unknown,
  prefix = "",
): string[] {
  if (!isRecord(reference)) {
    return [];
  }

  if (!isRecord(candidate)) {
    return [prefix || "(root)"];
  }

  const missing: string[] = [];

  for (const key of Object.keys(reference)) {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    if (!(key in candidate)) {
      missing.push(nextPrefix);
      continue;
    }

    missing.push(
      ...collectMissingPaths(
        (reference as UnknownRecord)[key],
        candidate[key],
        nextPrefix,
      ),
    );
  }

  return missing;
}

function collectExtraPaths(
  reference: unknown,
  candidate: unknown,
  prefix = "",
): string[] {
  if (!isRecord(candidate)) {
    return [];
  }

  if (!isRecord(reference)) {
    return [prefix || "(root)"];
  }

  const extra: string[] = [];

  for (const key of Object.keys(candidate)) {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    if (!(key in reference)) {
      extra.push(nextPrefix);
      continue;
    }

    extra.push(
      ...collectExtraPaths(
        (reference as UnknownRecord)[key],
        candidate[key],
        nextPrefix,
      ),
    );
  }

  return extra;
}

function assertLocaleParity(locale: Screen0LanguageCode, candidate: unknown) {
  const missingPaths = collectMissingPaths(EN_TRANSLATIONS, candidate);
  const extraPaths = collectExtraPaths(EN_TRANSLATIONS, candidate);

  if (!missingPaths.length && !extraPaths.length) {
    return;
  }

  const fragments: string[] = [];
  if (missingPaths.length) {
    fragments.push(`missing keys: ${missingPaths.join(", ")}`);
  }
  if (extraPaths.length) {
    fragments.push(`extra keys: ${extraPaths.join(", ")}`);
  }

  throw new Error(`Locale parity failed for "${locale}" (${fragments.join(" | ")}).`);
}

export const SCREEN0_MESSAGES: Record<Screen0LanguageCode, Screen0Messages> = {
  de: GERMAN_TRANSLATIONS,
  en: EN_TRANSLATIONS,
  es: ES_TRANSLATIONS,
  fr: FR_TRANSLATIONS,
  it: IT_TRANSLATIONS,
};

export function assertAllLocaleParity() {
  assertLocaleParity("en", SCREEN0_MESSAGES.en);
  assertLocaleParity("it", SCREEN0_MESSAGES.it);
  assertLocaleParity("es", SCREEN0_MESSAGES.es);
  assertLocaleParity("de", SCREEN0_MESSAGES.de);
  assertLocaleParity("fr", SCREEN0_MESSAGES.fr);
}

assertAllLocaleParity();

export function getLocaleMessages(
  languageCode: Screen0LanguageCode,
): Screen0Messages {
  return SCREEN0_MESSAGES[languageCode] ?? SCREEN0_MESSAGES.en;
}
